import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { contractsAPI } from '@/lib/api';
import { CheckCircle2, FileText, Loader2 } from 'lucide-react';

export default function ContractView() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    fetchContract();
  }, [token]);

  const fetchContract = async () => {
    try {
      const response = await contractsAPI.getPublic(token);
      setContract(response.data);
      if (response.data.status === 'accepted') {
        setAccepted(true);
      }
    } catch (error) {
      setError('Contract not found or has expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await contractsAPI.acceptPublic(token);
      setAccepted(true);
      setContract({ ...contract, status: 'accepted', accepted_at: new Date().toISOString() });
    } catch (error) {
      alert('Failed to accept contract. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  const getBillingLabel = (frequency) => {
    const labels = {
      'per-cut': 'Per Cut',
      'weekly': 'Weekly',
      'bi-weekly': 'Bi-Weekly',
      'monthly': 'Monthly'
    };
    return labels[frequency] || frequency;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-lime-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Contract Not Found</h2>
            <p className="text-gray-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-lime-600">{contract.company_name}</h1>
          <p className="text-gray-500 mt-2">Service Agreement</p>
        </div>

        {/* Accepted Banner */}
        {accepted && (
          <Card className="mb-6 border-green-200 bg-green-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <CheckCircle2 className="w-12 h-12 text-green-500" />
                <div>
                  <h3 className="text-xl font-bold text-green-800">Contract Accepted!</h3>
                  <p className="text-green-600">
                    Thank you! Your contract was accepted on {new Date(contract.accepted_at).toLocaleString()}.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contract Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Service Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Customer</p>
                <p className="font-semibold text-lg">{contract.customer_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Service Address</p>
                <p className="font-semibold">{contract.service_address}</p>
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-lime-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-gray-500">Rate</p>
                  <p className="text-2xl font-bold text-lime-600">${contract.rate}</p>
                  <p className="text-sm text-gray-500">{getBillingLabel(contract.billing_frequency)}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-gray-500">Start Date</p>
                  <p className="text-lg font-semibold">{contract.contract_start_date}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-gray-500">End Date</p>
                  <p className="text-lg font-semibold">{contract.contract_end_date || 'Ongoing'}</p>
                </div>
              </div>
            </div>

            <div>
              <p className="text-sm text-gray-500">Services</p>
              <p className="font-medium">{contract.services_description}</p>
            </div>

            {contract.customer_notes && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-600 font-medium">Note from {contract.company_name}:</p>
                <p className="text-blue-800 mt-1">{contract.customer_notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contract Terms */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Terms & Conditions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-50 p-4 rounded-lg max-h-96 overflow-y-auto">
              <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
                {contract.terms}
              </pre>
            </div>
          </CardContent>
        </Card>

        {/* Accept Button */}
        {!accepted && (
          <Card>
            <CardContent className="p-6">
              <p className="text-center text-gray-600 mb-4">
                By clicking "I Accept", you agree to the terms and conditions above and authorize {contract.company_name} to provide lawn care services at your property.
              </p>
              <Button
                className="w-full bg-lime-500 hover:bg-lime-600 text-black text-lg py-6"
                onClick={handleAccept}
                disabled={accepting}
              >
                {accepting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    I Accept This Contract
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-sm text-gray-400 mt-8">
          Questions? Contact {contract.company_name}
        </p>
      </div>
    </div>
  );
}
